import { Bugsnag } from 'lib/bugsnag';
import { getSearchParamsFromUrl } from 'utils/routing';

/**
 * Resume an /oauth/authorize request that was interrupted by signing in.
 *
 * GET /oauth/authorize bounces signed-out browsers here with the interrupted
 * URL in `continue`. The OAuth client owns app and account selection, so once
 * a session exists the remaining wizard steps do not apply.
 *
 * `continue` comes from the query string and is therefore attacker-controlled:
 * it is honoured only when it points at /oauth/authorize on the auth service.
 * The URL parsed for that check is the one navigated to, so no parsing
 * difference can open a gap between them.
 *
 * @returns whether navigation was started; the caller stops its own work if so.
 */
export function maybeResumeOAuthAuthorize(): boolean {
  const { continue_url: continueUrl } = getSearchParamsFromUrl(
    window.location.href
  );
  if (!continueUrl) return false;

  // Parsed separately from `continue` so a failure here is reported as our
  // misconfiguration rather than as a bad link.
  let apiOrigin: string;
  try {
    apiOrigin = new URL(import.meta.env.VITE_AUTH_SERVICE_API_URL).origin;
  } catch {
    reject('api-url-misconfigured', continueUrl);
    return false;
  }

  let target: URL;
  try {
    target = new URL(continueUrl);
  } catch {
    reject('not-a-url', continueUrl);
    return false;
  }

  if (target.origin !== apiOrigin) {
    reject('foreign-origin', continueUrl);
    return false;
  }
  if (target.pathname !== '/oauth/authorize') {
    reject('not-the-authorize-endpoint', continueUrl);
    return false;
  }

  window.location.href = target.toString();
  return true;
}

/**
 * Why a resume was refused. Closed, so a new reason has to choose one of the
 * two channels below rather than falling into the quiet one.
 */
type RefusalReason =
  | 'api-url-misconfigured'
  | 'not-a-url'
  | 'foreign-origin'
  | 'not-the-authorize-endpoint';

/**
 * Whether the environment has already been reported this page load.
 *
 * The wizard calls this twice per submit. A repeated breadcrumb is harmless;
 * a repeated error report is not, and the environment cannot change while the
 * page is open.
 */
let reportedMisconfiguration = false;

/**
 * Report a refused resume, at a weight matching what it means.
 *
 * A refused `continue` is a stale or crafted link -- the guard working -- so
 * it leaves a breadcrumb, which reaches Bugsnag only alongside a later error.
 * An unparseable VITE_AUTH_SERVICE_API_URL disables the resume for every
 * login in that environment and may never be followed by an error, so it is
 * notified instead.
 */
function reject(reason: RefusalReason, continueUrl: string): void {
  // Bugsnag is only started when there is an API key (see lib/bugsnag), and
  // calling it before that warns on every login in dev.
  if (!import.meta.env.VITE_BUGSNAG_API_KEY) return;

  // The origin only: the full URL can carry OAuth request parameters.
  const continueOrigin = safeOrigin(continueUrl);

  if (reason === 'api-url-misconfigured') {
    if (reportedMisconfiguration) return;
    reportedMisconfiguration = true;

    Bugsnag.notify(
      new Error(
        'OAuth authorize resume is disabled: VITE_AUTH_SERVICE_API_URL is not an absolute URL'
      ),
      (event) => event.addMetadata('resume', { continueOrigin })
    );
    return;
  }

  Bugsnag.leaveBreadcrumb('OAuth authorize resume refused', {
    reason,
    continueOrigin
  });
}

function safeOrigin(url: string): string {
  try {
    return new URL(url).origin;
  } catch {
    return 'unparseable';
  }
}

/**
 * Clears the once-per-page report. Exported only so tests are isolated from
 * each other: the flag outlives a test, and a second misconfiguration case
 * added later would otherwise see no report and pass for the wrong reason.
 */
export function resetMisconfigurationReport(): void {
  reportedMisconfiguration = false;
}
